import { useCallback, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { faDownload } from '@fortawesome/pro-solid-svg-icons'

import { Icon } from 'Components/Icon/Icon'

import type { PublicRequestReturnLabelSuccessProps } from './PublicRequestReturnLabel.types'

export const PublicRequestReturnLabelSuccess = (
  props: PublicRequestReturnLabelSuccessProps,
) => {
  const { trackingNumber, onBackToHome } = props
  const [copied, setCopied] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(trackingNumber).catch(() => {})
    setCopied(true)

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current)
    }

    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
  }, [trackingNumber])

  /** TODO: replace mock download with real API call to fetch return label PDF */
  const handleDownload = () => {
    const mockContent = `Return Label\nTracking Number: ${trackingNumber}\nGenerated: ${new Date().toLocaleString()}`
    const blob = new Blob([mockContent], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = `return-label-${trackingNumber}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="PublicRequestReturnLabelSuccess">
      <div className="PublicRequestReturnLabelSuccess-icon">
        <svg
          fill="none"
          height="64"
          viewBox="0 0 64 64"
          width="64"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="32" cy="32" fill="#d1fae5" r="32" />
          <path
            d="M20 32l8 8 16-16"
            stroke="#16a34a"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="4"
          />
        </svg>
      </div>

      <h1 className="PublicRequestReturnLabelSuccess-title">
        <FormattedMessage id="PublicRequestReturnLabel_success_title" />
      </h1>

      <p className="PublicRequestReturnLabelSuccess-description">
        <FormattedMessage id="PublicRequestReturnLabel_success_description" />
      </p>

      <div className="PublicRequestReturnLabelSuccess-downloadSection">
        <button
          className="PublicRequestReturnLabelSuccess-downloadButton"
          onClick={handleDownload}
          type="button"
        >
          <Icon icon={faDownload} size={16} />
          <FormattedMessage id="PublicRequestReturnLabel_success_download" />
        </button>

        <div className="PublicRequestReturnLabelSuccess-tracking">
          <span className="PublicRequestReturnLabelSuccess-trackingLabel">
            <FormattedMessage id="PublicRequestReturnLabel_success_trackingLabel" />
          </span>
          <div className="PublicRequestReturnLabelSuccess-trackingRow">
            <div className="PublicRequestReturnLabelSuccess-trackingValue">
              <code>{trackingNumber}</code>
            </div>
            <button
              className="PublicRequestReturnLabelSuccess-copyButton"
              onClick={handleCopy}
              type="button"
            >
              <FormattedMessage
                id={
                  copied
                    ? 'PublicRequestReturnLabel_success_copied'
                    : 'PublicRequestReturnLabel_success_copy'
                }
              />
            </button>
          </div>
        </div>
      </div>

      <div className="PublicRequestReturnLabelSuccess-footer">
        <button
          className="PublicRequestReturnLabelSuccess-backLink"
          onClick={onBackToHome}
          type="button"
        >
          <FormattedMessage id="PublicRequestReturnLabel_success_backToHome" />
        </button>
      </div>
    </div>
  )
}
